import { useState, useEffect, useCallback } from "react";
import { Loader2, CreditCard, Download, List, FileText } from "lucide-react";
import { toast } from "sonner";
import { PayByZelleButton } from "@/components/zelle/ZellePay";
import api from "@/lib/api";
import { money } from "@/lib/landingSim";
import { LedgerTable, StatementView } from "@/components/billing/LedgerTable";

const B = process.env.REACT_APP_BACKEND_URL;

const Zelle = ({ z, memo, amount }) => (
  <div className="flex items-center gap-3 bg-violet-50 border border-violet-200 rounded-lg p-3 text-sm" data-testid="my-statement-zelle">
    {z.has_qr && <img src={`${B}/api/public/zelle-qr.png?v=${z.updated_at || ""}`} alt="Zelle QR" className="w-20 h-20 bg-white rounded object-contain" />}
    <div><p className="font-bold text-violet-700">Pay with Zelle</p><p>{z.recipient_name}</p><p className="break-all">{z.recipient_contact}</p><p className="text-slate-500">Memo: <b>{memo}</b></p></div>
    <PayByZelleButton amount={amount} memo={memo} className="ml-auto shrink-0" />
  </div>
);

export const MyStatementsTab = () => {
  const [d, setD] = useState(null);
  const [period, setPeriod] = useState("");
  const [all, setAll] = useState(false);
  const [lg, setLg] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/api/customer/statements", { params: period ? { period } : {} }).then(r => setD(r.data)).catch(() => setD(false)), [period]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (all) api.get("/api/customer/statements/ledger").then(r => setLg(r.data)).catch(() => {}); }, [all, d]);
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) return;
    (async () => {
      for (let i = 0; i < 10; i++) {
        try { const { data } = await api.get(`/api/customer/statements/pay/${sid}`); if (data.payment_status === "paid") { toast.success("Payment received — receipt emailed"); break; } } catch { /* retry */ }
        await new Promise(r => setTimeout(r, 2500));
      }
      window.history.replaceState({}, "", "/dashboard?tab=statements"); load();
    })();
  }, [load]);
  const pay = async () => {
    setBusy(true);
    try { const { data } = await api.post("/api/customer/statements/pay", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (err) { toast.error(err.response?.data?.detail || "Couldn't start the payment"); setBusy(false); }
  };
  if (d === false) return <p className="text-sm text-slate-500">Statements aren't available right now.</p>;
  if (!d) return <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto my-10" />;
  const st = d.statement;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4 max-w-3xl" data-testid="my-statements">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" />Monthly Statement</h2><p className="text-sm text-slate-500">{d.account.active && d.account.monthly_amount > 0 ? `${d.account.description} · ${money(d.account.monthly_amount)}/month` : "Your ShopAdvisor™ account"}</p></div>
        <select value={st.period} onChange={e => setPeriod(e.target.value)} className="h-9 border border-slate-200 rounded-md px-2 text-sm" data-testid="my-statement-period">{d.periods.map(p => <option key={p} value={p}>{p}</option>)}</select>
      </div>
      <StatementView st={st} />
      <div className="flex flex-wrap gap-2">
        {st.account_balance >= 0.5 && <button onClick={pay} disabled={busy} className="bg-green-600 hover:bg-green-500 text-white text-sm font-bold px-4 py-2.5 rounded-lg flex items-center gap-2 disabled:opacity-50" data-testid="my-statement-pay">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}Pay {money(st.account_balance)} by card</button>}
        <a href={`${B}/api/public/statements/${st.token}/pdf`} className="border border-slate-300 text-sm font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2" data-testid="my-statement-pdf"><Download className="w-4 h-4" />Download PDF</a>
        <button onClick={() => setAll(a => !a)} className="border border-blue-300 text-blue-700 hover:bg-blue-50 text-sm font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2" data-testid="my-statement-view-all"><List className="w-4 h-4" />{all ? "Hide all charges" : "View all charges"}</button>
      </div>
      {st.account_balance >= 0.5 && <p className="text-[11px] text-slate-400 -mt-2">Sales tax may be added at card checkout where required.</p>}
      {d.zelle && st.account_balance >= 0.5 && <Zelle z={d.zelle} memo={st.statement_number} amount={st.account_balance} />}
      {all && (lg ? <div className="border-t border-slate-100 pt-4"><p className="font-bold text-slate-900 mb-2">Everything you've been charged and paid</p><LedgerTable lg={lg} /></div> : <Loader2 className="w-5 h-5 animate-spin text-blue-600" />)}
    </div>
  );
};
